import { Alert, Badge, Button, EmptyState, Field, Input } from '@personachat/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Brain, Download, Eye, FileLock2, Pencil, Trash2 } from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { api } from '../../api/client';
import { ErreurApi, lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { enregistrer, nomDate } from '../../lib/fichiers';
import { formatDate, formatDateTime } from '../../lib/format';

type Memoires = components['schemas']['StudentMemories'];
type VueMemoire = components['schemas']['MemoryView'];
type SectionMemoire = components['schemas']['MemorySection'];
type Message = components['schemas']['ConversationMessage'];

/** « LE_COACH_IKIGAI » → « Le coach ikigai ». */
export function libelleEtiquette(tag: string | null): string {
  if (!tag) return 'Notes sans titre';
  const texte = tag.replace(/_/g, ' ').toLowerCase().trim();
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

/**
 * Motif de consultation (cahier des charges §7.9) : une conversation ou une
 * mémoire est une donnée personnelle, son ouverture est inscrite au journal
 * avec ce motif.
 */
export function FormulaireMotif({
  explication,
  bouton,
  onValider,
}: {
  explication: ReactNode;
  bouton: string;
  onValider: (motif: string) => Promise<void>;
}) {
  const id = useId();
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const valide = motif.trim().length >= 3;

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await onValider(motif.trim());
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form className="motif" onSubmit={valider}>
      <div className="motif__explication">
        <FileLock2 size={18} aria-hidden="true" />
        <div>{explication}</div>
      </div>
      {erreur && <Alert>{erreur}</Alert>}
      <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, inscrit au journal d’audit avec votre nom.">
        <Input
          id={`${id}-motif`}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          maxLength={300}
          placeholder="Ex. : ticket support du 29/09, réponse incohérente du coach"
        />
      </Field>
      <div>
        <Button type="submit" variant="primary" size="sm" icon={<Eye size={14} aria-hidden="true" />} loading={envoi} disabled={!valide}>
          {bouton}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Conversation
// ---------------------------------------------------------------------------

/** Lecture d'une conversation (lecture seule), après le motif. */
export function LectureConversation({
  eleveId,
  conversationId,
  titre,
  onClose,
}: {
  eleveId: string;
  conversationId: string;
  titre: string;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [donnees, setDonnees] = useState<{
    titre: string | null;
    coach: string | null;
    projet: string | null;
    creee: string;
    messages: Message[];
  } | null>(null);

  const ouvrir = async (motif: string) => {
    const r = await lire(
      api.POST('/api/admin/users/{id}/conversations/{conversationId}/read', {
        params: { path: { id: eleveId, conversationId } },
        body: { motif },
      })
    );
    setDonnees({
      titre: r.conversation.title,
      coach: r.conversation.personality?.name ?? null,
      projet: r.conversation.project?.name ?? null,
      creee: r.conversation.createdAt,
      messages: r.messages,
    });
    void client.invalidateQueries({ queryKey: ['audit', eleveId] });
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={donnees?.titre || titre} width={760}>
      {!donnees ? (
        <FormulaireMotif
          explication="Les messages d’une élève sont des données personnelles : lisez-les seulement pour traiter une demande (support, signalement, RGPD)."
          bouton="Lire la conversation"
          onValider={ouvrir}
        />
      ) : (
        <div className="stack">
          <p className="note" style={{ margin: 0 }}>
            {donnees.coach ?? 'Coach supprimé'}
            {donnees.projet && ` · projet « ${donnees.projet} »`} · commencée le {formatDate(donnees.creee)} · {donnees.messages.length} message
            {donnees.messages.length > 1 ? 's' : ''} · lecture inscrite au journal
          </p>
          <ol className="fil" aria-label="Messages">
            {donnees.messages.map((m) => (
              <li key={m.id} className={`fil__message fil__message--${m.role.toLowerCase()}`}>
                <div className="fil__auteur">
                  {m.role === 'USER' ? 'Élève' : m.role === 'ASSISTANT' ? (donnees.coach ?? 'Coach') : 'Système'}
                  <span className="fil__date">{formatDateTime(m.createdAt)}</span>
                </div>
                <div className="fil__texte">{m.content}</div>
                {(m.images.length > 0 || m.attachments.length > 0) && (
                  <div className="fil__pieces">
                    {m.images.map((url, i) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer">
                        Image {i + 1}
                      </a>
                    ))}
                    {m.attachments.map((a) => (
                      <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                        {a.name}
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
          {donnees.messages.length === 0 && <p className="texte-aide">Aucun message dans cette conversation.</p>}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mémoire
// ---------------------------------------------------------------------------

interface Cible {
  projectId: string | null;
  version: string;
  index: number;
  section: SectionMemoire;
  nom: string;
}

/**
 * Mémoire IA d'une élève (cahier des charges §7.9) : ouverte avec un motif,
 * lue section par section, corrigée ou effacée à la demande de l'élève.
 */
export function MemoiresEleve({ eleveId }: { eleveId: string }) {
  const toast = useToast();
  const client = useQueryClient();
  const [motifLecture, setMotifLecture] = useState<string | null>(null);
  const [memoires, setMemoires] = useState<Memoires | null>(null);
  const [cible, setCible] = useState<{ mode: 'corriger' | 'effacer'; cible: Cible } | null>(null);
  const [avertissement, setAvertissement] = useState<string | null>(null);

  const lireMemoires = async (motif: string) => {
    const r = await lire(api.POST('/api/admin/users/{id}/memories/read', { params: { path: { id: eleveId } }, body: { motif } }));
    setMemoires(r);
    setMotifLecture(motif);
    void client.invalidateQueries({ queryKey: ['audit', eleveId] });
  };

  const corriger = async (c: Cible, contenu: string | null, motif: string) => {
    try {
      const r = await lire(
        api.PATCH('/api/admin/users/{id}/memories', {
          params: { path: { id: eleveId } },
          body: { motif, projectId: c.projectId, version: c.version, sectionIndex: c.index, content: contenu },
        })
      );
      setMemoires(r);
      setAvertissement(null);
      toast.succes(contenu === null ? 'Section effacée de la mémoire.' : 'Section corrigée.');
      void client.invalidateQueries({ queryKey: ['audit', eleveId] });
      void client.invalidateQueries({ queryKey: ['memoires'] });
    } catch (err) {
      // L'IA a enrichi la mémoire entre-temps : on relit la version à jour avant toute correction.
      if (err instanceof ErreurApi && err.code === 'MEMORY_CHANGED' && motifLecture) {
        await lireMemoires(motifLecture);
        setAvertissement('La mémoire a changé depuis son ouverture (l’IA l’a complétée). Voici la version à jour : refaites la correction.');
        return;
      }
      throw err;
    }
  };

  if (!memoires) {
    return (
      <FormulaireMotif
        explication="La mémoire contient ce que l’IA a retenu de l’élève au fil des conversations. Ouvrez-la seulement pour traiter une demande (support, rectification ou effacement RGPD)."
        bouton="Ouvrir la mémoire"
        onValider={lireMemoires}
      />
    );
  }

  const blocs: Array<{ id: string; nom: string; detail?: string; projectId: string | null; vue: VueMemoire }> = [
    { id: 'perso', nom: 'Mémoire personnelle', projectId: null, vue: memoires.personal },
    ...memoires.projects
      .filter((p) => p.memory.sections.length > 0)
      .map((p) => ({
        id: p.id,
        nom: `Projet « ${p.name} »`,
        detail: `${p.archived ? 'archivé · ' : ''}mis à jour le ${formatDate(p.updatedAt)}`,
        projectId: p.id,
        vue: p.memory,
      })),
  ];
  const vide = blocs.every((b) => b.vue.sections.length === 0);

  return (
    <div className="stack">
      <p className="note" style={{ margin: 0 }}>
        Mémoire {memoires.memoryEnabled ? 'activée' : 'désactivée'} par l’élève · lecture inscrite au journal (« {motifLecture} »).
      </p>
      {avertissement && <Alert tone="info">{avertissement}</Alert>}
      {vide ? (
        <EmptyState icon={<Brain size={24} aria-hidden="true" />} title="Mémoire vide" text="L’IA n’a encore rien retenu pour cette élève." />
      ) : (
        blocs
          .filter((b) => b.vue.sections.length > 0)
          .map((b) => (
            <section key={b.id} className="memoire">
              <h4 className="memoire__titre">
                {b.nom}
                {b.detail && <span className="muted small"> · {b.detail}</span>}
              </h4>
              {b.vue.sections.map((s, i) => (
                <article key={`${s.tag ?? 'sans'}-${i}`} className="memoire__section">
                  <div className="memoire__entete">
                    <div>
                      <strong>{libelleEtiquette(s.tag)}</strong>
                      {s.meta && <span className="muted small"> · {s.meta.replace(/^Date:\s*/, '')}</span>}
                    </div>
                    <div className="row">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Pencil size={13} aria-hidden="true" />}
                        onClick={() => setCible({ mode: 'corriger', cible: { projectId: b.projectId, version: b.vue.version, index: i, section: s, nom: b.nom } })}
                      >
                        Corriger
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 size={13} aria-hidden="true" />}
                        onClick={() => setCible({ mode: 'effacer', cible: { projectId: b.projectId, version: b.vue.version, index: i, section: s, nom: b.nom } })}
                      >
                        Effacer
                      </Button>
                    </div>
                  </div>
                  <div className="memoire__lignes">{s.lines.join('\n')}</div>
                </article>
              ))}
            </section>
          ))
      )}

      {cible?.mode === 'corriger' && (
        <CorrectionSection cible={cible.cible} onClose={() => setCible(null)} onValider={(contenu, motif) => corriger(cible.cible, contenu, motif)} />
      )}
      <ConfirmDialog
        open={cible?.mode === 'effacer'}
        onOpenChange={(o) => !o && setCible(null)}
        title="Effacer une section de la mémoire"
        confirmLabel="Effacer"
        tone="danger"
        motif
        onConfirm={async (motif) => {
          if (cible) await corriger(cible.cible, null, motif);
        }}
      >
        {cible && (
          <p>
            La section « {libelleEtiquette(cible.cible.section.tag)} » de la {cible.cible.nom.toLowerCase()} sera effacée : l’IA ne s’en servira
            plus. Son contenu n’est pas gardé au journal.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}

function CorrectionSection({ cible, onClose, onValider }: { cible: Cible; onClose: () => void; onValider: (contenu: string, motif: string) => Promise<void> }) {
  const id = useId();
  const [contenu, setContenu] = useState(cible.section.lines.join('\n'));
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const valide = motif.trim().length >= 3 && contenu.trim().length > 0 && contenu !== cible.section.lines.join('\n');

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await onValider(contenu, motif.trim());
      onClose();
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal open onOpenChange={(o) => !o && !envoi && onClose()} title={`Corriger « ${libelleEtiquette(cible.section.tag)} »`} width={640}>
      <form className="stack" onSubmit={valider}>
        <p className="texte-aide">{cible.nom}. Gardez une idée par ligne (« • … ») : c’est ce que l’IA relira.</p>
        {erreur && <Alert>{erreur}</Alert>}
        <Field id={`${id}-contenu`} label="Contenu">
          <textarea id={`${id}-contenu`} className="ui-input textarea textarea--texte" rows={8} value={contenu} onChange={(e) => setContenu(e.target.value)} />
        </Field>
        <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, inscrit au journal d’audit.">
          <Input id={`${id}-motif`} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={300} placeholder="Ex. : demande de rectification de l’élève du 29/09" />
        </Field>
        <div className="modal__footer modal__footer--inline">
          <Button onClick={onClose} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi} disabled={!valide}>
            Enregistrer la correction
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Export RGPD
// ---------------------------------------------------------------------------

/** Export de toutes les données de l'élève (droit d'accès, portabilité), motif au journal. */
export function ExportRgpd({ eleveId, email }: { eleveId: string; email: string }) {
  const toast = useToast();
  const client = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  return (
    <>
      <Button size="sm" icon={<Download size={14} aria-hidden="true" />} onClick={() => setOuvert(true)}>
        Exporter ses données (RGPD)
      </Button>
      <ConfirmDialog
        open={ouvert}
        onOpenChange={setOuvert}
        title="Exporter les données de l’élève"
        confirmLabel="Télécharger"
        motif
        onConfirm={async (motif) => {
          const fichier = await lire(api.POST('/api/admin/users/{id}/export', { params: { path: { id: eleveId } }, body: { motif }, parseAs: 'blob' }));
          enregistrer(fichier, nomDate(`donnees-${email.split('@')[0]}`, 'json'));
          toast.succes('Export téléchargé. Transmettez-le à l’élève par un canal sûr, puis supprimez-le.');
          void client.invalidateQueries({ queryKey: ['audit', eleveId] });
        }}
      >
        <p>
          Un fichier JSON avec le profil, les crédits, les achats, les conversations, les mémoires et la liste des images et vidéos de {email}.
          Réponse à une demande d’accès ou de portabilité (RGPD, articles 15 et 20).
        </p>
        <p>Le fichier contient des données personnelles : ne l’envoyez qu’à l’élève, par un canal sûr.</p>
      </ConfirmDialog>
    </>
  );
}

/** Pastille « mémoire activée / désactivée » d'une ligne de liste. */
export function PastilleMemoire({ active }: { active: boolean }) {
  return <Badge tone={active ? 'success' : 'neutral'}>{active ? 'Activée' : 'Désactivée'}</Badge>;
}
